import { Component } from '@theme/component';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig, logError } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorEmpty - The empty input error element.
 * @property {HTMLElement} cartDiscountSuccess - The success element.
 * @property {HTMLButtonElement} cartDiscountSubmitBtn - The submit button element.
 * @property {HTMLElement} cartDiscountBtnText - The button text element.
 * @property {HTMLElement} cartDiscountBtnSpinner - The button spinner element.
 * @property {HTMLButtonElement} cartDiscountDeleteBtn - The delete discount button element.
 * @property {HTMLElement} cartDiscountHint - The hint message element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
    'cartDiscountErrorEmpty',
    'cartDiscountSuccess',
    'cartDiscountSubmitBtn',
    'cartDiscountBtnText',
    'cartDiscountBtnSpinner',
    'cartDiscountDeleteBtn',
    'cartDiscountHint',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string | null} */
  #errorTemplate = null;

  /**
   * Escapes HTML special characters to prevent XSS when injecting user input.
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles applying a discount code to the cart.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorEmpty,
      cartDiscountSuccess,
      cartDiscountSubmitBtn,
      cartDiscountBtnText,
      cartDiscountBtnSpinner,
      cartDiscountHint,
    } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();

    // Hide previous messages
    cartDiscountError.classList.add('hidden');
    cartDiscountError.classList.remove('flex');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    cartDiscountErrorEmpty.classList.add('hidden');
    cartDiscountSuccess.classList.add('hidden');
    cartDiscountSuccess.classList.remove('flex');
    cartDiscountHint.classList.add('hidden');

    if (!discountCodeValue) {
      this.#handleDiscountError('empty');
      return;
    }

    const abortController = this.#createAbortController();

    // Show loading spinner
    cartDiscountBtnText.classList.add('invisible');
    cartDiscountBtnSpinner.classList.remove('hidden');
    cartDiscountSubmitBtn.disabled = true;

    try {
      // Only clear when an existing discount is applied; otherwise skip to avoid
      // an unnecessary empty-discount round-trip that can fail silently.
      if (this.dataset.hasDiscount === 'true') {
        await this.#clearDiscount(abortController);
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: discountCodeValue,
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const isInvalid = data.discount_codes.find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        }
      );

      if (isInvalid) {
        this.#handleDiscountError('discount_code', discountCodeValue);
        return;
      }

      // Check for shipping-only discount
      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (section) {
        const newInput = section.querySelector('input[name="discount"]');
        if (newInput instanceof HTMLInputElement && !newInput.value) {
          if (
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping', discountCodeValue);
            return;
          }
        }
      }

      // Dispatch event to trigger section re-render with correct discount data
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        logError('cart-discount:applyDiscount', error);
      }
    } finally {
      this.#activeFetch = null;
      // Hide loading spinner
      cartDiscountBtnText.classList.remove('invisible');
      cartDiscountBtnSpinner.classList.add('hidden');
      cartDiscountSubmitBtn.disabled = false;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  removeDiscount = async () => {
    const {
      cartDiscountSuccess,
      cartDiscountDeleteBtn,
    } = this.refs;

    const abortController = this.#createAbortController();

    // Disable delete button during request
    cartDiscountDeleteBtn.disabled = true;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: '',
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Dispatch event to update cart prices
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      // Hide success message
      cartDiscountSuccess.classList.add('hidden');
      cartDiscountSuccess.classList.remove('flex');

      // Clear the input field
      const discountInput = this.querySelector('input[name="discount"]');
      if (discountInput instanceof HTMLInputElement) {
        discountInput.value = '';
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        logError('cart-discount:removeDiscount', error);
      }
    } finally {
      this.#activeFetch = null;
      cartDiscountDeleteBtn.disabled = false;
    }
  };

  /**
   * Clears all applied discount codes without triggering a section re-render.
   * Updates the cart total in the UI directly.
   *
   * @param {AbortController} abortController - The abort controller for the request.
   */
  async #clearDiscount(abortController) {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: '',
        sections: [this.dataset.sectionId],
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, {
      ...config,
      signal: abortController.signal,
    });

    const data = await response.json();
    this.#updateCartTotal(data);
  }

  /**
   * Updates the cart total display from cart API response data.
   *
   * @param {{ sections?: Record<string, string> }} data - The cart API response.
   */
  #updateCartTotal(data) {
    const sectionId = this.dataset.sectionId;
    if (!data.sections || !sectionId) return;
    const sectionHtml = data.sections[sectionId];
    if (!sectionHtml) return;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newTotal = parsed.querySelector('[ref="cartTotal"]');
    const currentTotal = document.querySelector('[ref="cartTotal"]');
    if (newTotal && currentTotal) {
      currentTotal.innerHTML = newTotal.innerHTML;
    }
    // Order Summary(cart-order-summary.liquid)도 버튼 금액과 같은 값이라 같이 바꾼다
    const newSummary = parsed.querySelector('[ref="cartOrderSummary"]');
    const currentSummary = document.querySelector('[ref="cartOrderSummary"]');
    if (newSummary && currentSummary) {
      currentSummary.replaceWith(newSummary);
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'empty'} type - The type of discount error.
   * @param {string} [code] - The discount code that failed.
   */
  #handleDiscountError(type, code) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorEmpty,
      cartDiscountHint,
    } = this.refs;
    cartDiscountError.classList.remove('hidden');
    cartDiscountError.classList.add('flex');
    cartDiscountHint.classList.add('hidden');

    if (type === 'discount_code') {
      const errorEl = cartDiscountErrorDiscountCode;
      if (this.#errorTemplate === null) {
        this.#errorTemplate = errorEl.innerHTML;
      }
      errorEl.innerHTML = this.#errorTemplate.replace('__CODE__', this.#escapeHtml(code ?? ''));
      errorEl.classList.remove('hidden');
    } else if (type === 'empty') {
      cartDiscountErrorEmpty.classList.remove('hidden');
    } else {
      cartDiscountErrorShipping.classList.remove('hidden');
    }
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
